/**
 * Storefront GraphQL client for the Sep Design PDP.
 *
 * Config: window.SEP_STOREFRONT (domain, token, apiVersion) from
 * snippets/sep-pdp-storefront-config.liquid. When no token, enabled() is false
 * and callers fall back to Section Rendering / Ajax .json.
 *
 * Caching: memory + localStorage, 10 min TTL, in-flight dedupe, 429 backoff.
 * Failed / 429 responses are never cached.
 */
(function (global) {
  if (global.sepStorefront && global.sepStorefront._ready) return

  var cfg = global.SEP_STOREFRONT || {}
  var TTL_MS = 10 * 60 * 1000
  var PREFIX = 'sepSf:v1:'
  var MAX_429_RETRIES = 3
  var memory = new Map()
  var inflight = new Map()

  function enabled() {
    return !!(cfg && cfg.token && cfg.domain)
  }

  function endpoint() {
    var ver = (cfg && cfg.apiVersion) || '2025-07'
    return 'https://' + cfg.domain + '/api/' + ver + '/graphql.json'
  }

  function hash(str) {
    var h = 5381
    for (var i = 0; i < str.length; i++) {
      h = (h * 33) ^ str.charCodeAt(i)
    }
    return (h >>> 0).toString(36)
  }

  function keyFor(query, variables) {
    return PREFIX + hash(query) + ':' + hash(JSON.stringify(variables || {}))
  }

  function isEmptyCatalogPayload(data) {
    if (data == null) return true
    if (Object.prototype.hasOwnProperty.call(data, 'product') && !data.product) return true
    if (Object.prototype.hasOwnProperty.call(data, 'collection') && !data.collection) return true
    return false
  }

  function read(key) {
    var now = Date.now()
    var mem = memory.get(key)
    if (mem) {
      if (now - mem.ts < TTL_MS && !isEmptyCatalogPayload(mem.data)) return mem.data
      memory.delete(key)
    }
    try {
      var raw = localStorage.getItem(key)
      if (!raw) return null
      var parsed = JSON.parse(raw)
      if (!parsed || now - parsed.ts >= TTL_MS || isEmptyCatalogPayload(parsed.data)) {
        localStorage.removeItem(key)
        return null
      }
      memory.set(key, parsed)
      return parsed.data
    } catch (e) {
      return null
    }
  }

  function write(key, data) {
    if (isEmptyCatalogPayload(data)) return
    var entry = { ts: Date.now(), data: data }
    memory.set(key, entry)
    try {
      localStorage.setItem(key, JSON.stringify(entry))
    } catch (e) {
      try {
        for (var i = localStorage.length - 1; i >= 0; i--) {
          var k = localStorage.key(i)
          if (k && k.indexOf(PREFIX) === 0) localStorage.removeItem(k)
        }
        localStorage.setItem(key, JSON.stringify(entry))
      } catch (e2) {}
    }
  }

  function clearKey(key) {
    memory.delete(key)
    try {
      localStorage.removeItem(key)
    } catch (e) {}
  }

  function graphql(query, variables, retryCount) {
    if (!enabled()) return Promise.reject(new Error('storefront disabled'))
    retryCount = retryCount || 0
    var key = keyFor(query, variables)
    var cached = read(key)
    if (cached != null) return Promise.resolve(cached)
    if (inflight.has(key)) return inflight.get(key)

    var promise = fetch(endpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    })
      .then(function (res) {
        if (res.status === 429) {
          var e = new Error('storefront 429')
          e.status = 429
          throw e
        }
        if (!res.ok) {
          var e2 = new Error('storefront ' + res.status)
          e2.status = res.status
          throw e2
        }
        return res.json()
      })
      .then(function (json) {
        if (json && json.errors && json.errors.length) {
          var err = new Error('storefront graphql error')
          err.graphqlErrors = json.errors
          throw err
        }
        var data = json.data
        // Unpublished / not-on-channel lookups return product:null. Never cache that —
        // merchants publish later and a 10min localStorage miss keeps size links dead.
        if (
          data &&
          ((Object.prototype.hasOwnProperty.call(data, 'product') && !data.product) ||
            (Object.prototype.hasOwnProperty.call(data, 'collection') && !data.collection))
        ) {
          inflight.delete(key)
          return data
        }
        write(key, data)
        inflight.delete(key)
        return data
      })
      .catch(function (err) {
        clearKey(key)
        inflight.delete(key)
        if (err && err.status === 429 && retryCount < MAX_429_RETRIES) {
          var delay = 1500 * Math.pow(2, retryCount)
          var retryPromise = new Promise(function (resolve, reject) {
            setTimeout(function () {
              if (inflight.get(key) === retryPromise) inflight.delete(key)
              graphql(query, variables, retryCount + 1).then(resolve, reject)
            }, delay)
          })
          inflight.set(key, retryPromise)
          return retryPromise
        }
        throw err
      })

    inflight.set(key, promise)
    return promise
  }

  function gidToId(gid) {
    if (gid == null) return null
    var s = String(gid)
    var m = s.match(/(\d+)(?:[?#].*)?$/)
    return m ? Number(m[1]) : s
  }

  function moneyToPriceString(money) {
    if (!money || money.amount == null) return null
    var n = Number(money.amount)
    if (isNaN(n)) return null
    return n.toFixed(2)
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  function imageUrl(url, width) {
    if (!url) return ''
    try {
      var u = new URL(url, (typeof location !== 'undefined' && location.href) || 'https://example.com')
      if (width) u.searchParams.set('width', String(width))
      return u.toString()
    } catch (e) {
      return url
    }
  }

  function tagValue(tags, prefix) {
    var list = tags || []
    for (var i = 0; i < list.length; i++) {
      var t = list[i]
      if (t && t.indexOf(prefix) === 0) return t.slice(prefix.length)
    }
    return ''
  }

  function frameCollectionHandleFromTags(tags) {
    var list = tags || []
    var raw = ''
    for (var i = 0; i < list.length; i++) {
      var t = list[i]
      if (!t) continue
      if (t.indexOf('_oph_frame_name:') === 0) raw = t.slice('_oph_frame_name:'.length)
      else if (t.indexOf('_rx_frame_name:') === 0) raw = t.slice('_rx_frame_name:'.length)
      else if (t.indexOf('_frame_name:') === 0) raw = t.slice('_frame_name:'.length)
    }
    if (!raw) return ''
    return getSizeBaseHandle(raw.replace(/_/g, '-'))
  }

  function sizeSelectorFitHref() {
    return '#pdp-fit-modal'
  }

  function getSizeKeyFromHandle(handle) {
    if (!handle) return 'std'
    if (handle.indexOf('-xxl') !== -1) return 'xxl'
    if (handle.indexOf('-xl') !== -1) return 'xl'
    return 'std'
  }

  function getSizeBaseHandle(handle) {
    if (!handle) return ''
    return handle
      .replace('-xxl-rx', '-rx')
      .replace('-xl-rx', '-rx')
      .replace('-xxl-z87', '-z87')
      .replace('-xl-z87', '-z87')
      .replace('-xxl', '')
      .replace('-xl', '')
  }

  function getSizeHandleForKey(baseHandle, sizeKey) {
    if (!baseHandle) return ''
    var isRx = baseHandle.indexOf('-rx') !== -1
    var isZ87 = /-z87$/.test(baseHandle)
    if (sizeKey === 'std') return baseHandle
    if (sizeKey === 'xl') {
      if (isRx) return baseHandle.replace('-rx', '-xl-rx')
      if (isZ87) return baseHandle.replace(/-z87$/, '-xl-z87')
      return baseHandle + '-xl'
    }
    if (sizeKey === 'xxl') {
      if (isRx) return baseHandle.replace('-rx', '-xxl-rx')
      if (isZ87) return baseHandle.replace(/-z87$/, '-xxl-z87')
      return baseHandle + '-xxl'
    }
    return baseHandle
  }

  function getSizeFamilyHandles(handle) {
    var base = getSizeBaseHandle(handle)
    var isZ87 = /-z87$/.test(base)
    return {
      base: base,
      current: getSizeKeyFromHandle(handle),
      std: getSizeHandleForKey(base, 'std'),
      xl: getSizeHandleForKey(base, 'xl'),
      xxl: isZ87 ? '' : getSizeHandleForKey(base, 'xxl')
    }
  }

  function counterpartHandleFrom(handle) {
    var family = getSizeFamilyHandles(handle)
    if (family.current === 'std') return family.xl
    return family.std
  }

  function toRegularHandle(handle) {
    if (!handle) return ''
    return handle
      .replace(/-xxl-z87$/, '-xxl')
      .replace(/-xl-z87$/, '-xl')
      .replace(/-z87$/, '')
  }

  function toSafetyHandle(handle) {
    if (!handle) return ''
    if (/-z87$/.test(handle)) return handle
    return handle + '-z87'
  }

  var PRODUCT_QUERY =
    'query ProductByHandle($handle: String!) {' +
    '  product(handle: $handle) {' +
    '    id handle title productType descriptionHtml tags availableForSale' +
    '    featuredImage { url altText width height }' +
    '    subtitle: metafield(namespace: "custom", key: "product_subtitle") { value }' +
    '    mainCollection: metafield(namespace: "custom", key: "main_collection") {' +
    '      reference { ... on Collection { handle title } }' +
    '    }' +
    '    linkedEyewear: metafield(namespace: "custom", key: "linked_eyewear_accessory") {' +
    '      reference {' +
    '        ... on Product {' +
    '          handle tags' +
    '          mainCollection: metafield(namespace: "custom", key: "main_collection") {' +
    '            reference { ... on Collection { handle title } }' +
    '          }' +
    '        }' +
    '      }' +
    '      references(first: 5) {' +
    '        nodes {' +
    '          ... on Product {' +
    '            handle tags' +
    '            mainCollection: metafield(namespace: "custom", key: "main_collection") {' +
    '              reference { ... on Collection { handle title } }' +
    '            }' +
    '          }' +
    '        }' +
    '      }' +
    '    }' +
    '    media(first: 50) {' +
    '      nodes {' +
    '        alt mediaContentType' +
    '        ... on MediaImage { image { url altText width height } }' +
    '        ... on Video { sources { url mimeType } previewImage { url altText } }' +
    '        ... on Model3d { previewImage { url altText } }' +
    '      }' +
    '    }' +
    '    variants(first: 250) {' +
    '      nodes {' +
    '        id title sku availableForSale' +
    '        price { amount } compareAtPrice { amount }' +
    '        selectedOptions { name value }' +
    '        image { url altText width height }' +
    '      }' +
    '      pageInfo { hasNextPage endCursor }' +
    '    }' +
    '  }' +
    '}'

  // Rx XXL frames carry 372 variants; the photochromic ones sit past the first 250.
  var PRODUCT_VARIANTS_PAGE_QUERY =
    'query ProductVariantsPage($handle: String!, $after: String!) {' +
    '  product(handle: $handle) {' +
    '    variants(first: 250, after: $after) {' +
    '      nodes {' +
    '        id title sku availableForSale' +
    '        price { amount } compareAtPrice { amount }' +
    '        selectedOptions { name value }' +
    '        image { url altText width height }' +
    '      }' +
    '      pageInfo { hasNextPage endCursor }' +
    '    }' +
    '  }' +
    '}'

  var COLLECTION_QUERY =
    'query CollectionByHandle($handle: String!) {' +
    '  collection(handle: $handle) {' +
    '    id handle title' +
    '    products(first: 50) {' +
    '      nodes {' +
    '        id handle title productType tags availableForSale' +
    '        featuredImage { url altText width height }' +
    '        images(first: 60) { nodes { url altText width height } }' +
    '        media(first: 30) { nodes { alt mediaContentType ... on Video { previewImage { url altText } } } }' +
    '        variants(first: 250) {' +
    '          nodes {' +
    '            id title sku availableForSale' +
    '            price { amount } compareAtPrice { amount }' +
    '            selectedOptions { name value }' +
    '            image { url altText width height }' +
    '          }' +
    '        }' +
    '      }' +
    '    }' +
    '  }' +
    '}'

  function normalizeMedia(nodes) {
    return (nodes || []).map(function (m) {
      var type = (m.mediaContentType || '').toLowerCase()
      var out = { alt: m.alt || '', media_type: type === 'image' ? 'image' : type === 'video' ? 'video' : type === 'model_3d' ? 'model' : type }
      if (m.image) {
        out.src = m.image.url
        out.width = m.image.width
        out.height = m.image.height
        if (!out.alt && m.image.altText) out.alt = m.image.altText
      } else if (m.previewImage) {
        out.src = m.previewImage.url
        if (!out.alt && m.previewImage.altText) out.alt = m.previewImage.altText
      }
      if (m.sources && m.sources.length) {
        out.sources = m.sources.map(function (s) {
          return { url: s.url, mime_type: s.mimeType }
        })
      }
      return out
    })
  }

  function normalizeVariant(v) {
    var opts = v.selectedOptions || []
    var featured = v.image
      ? { src: v.image.url, alt: v.image.altText || '', width: v.image.width, height: v.image.height }
      : null
    return {
      id: gidToId(v.id),
      title: v.title,
      sku: v.sku || '',
      available: !!v.availableForSale,
      price: moneyToPriceString(v.price),
      compare_at_price: moneyToPriceString(v.compareAtPrice),
      option1: opts[0] ? opts[0].value : null,
      option2: opts[1] ? opts[1].value : null,
      option3: opts[2] ? opts[2].value : null,
      options: opts.map(function (o) {
        return o.value
      }),
      featured_image: featured,
      image_id: null
    }
  }

  function normalizeProduct(p) {
    if (!p) return null
    var variants = (p.variants && p.variants.nodes ? p.variants.nodes : []).map(normalizeVariant)
    var media = normalizeMedia(p.media && p.media.nodes)
    var images = []
    if (p.featuredImage && p.featuredImage.url) {
      images.push({ src: p.featuredImage.url, alt: p.featuredImage.altText || '' })
    }
    media.forEach(function (m) {
      if (m.src && !images.some(function (im) { return im.src === m.src })) {
        images.push({ src: m.src, alt: m.alt || '' })
      }
    })
    variants.forEach(function (v) {
      if (v.featured_image && v.featured_image.src && !images.some(function (im) { return im.src === v.featured_image.src })) {
        images.push({ src: v.featured_image.src, alt: v.featured_image.alt || '' })
      }
    })

    var metafields = {}
    if (p.subtitle && p.subtitle.value != null) metafields.product_subtitle = p.subtitle.value
    var mainCol = p.mainCollection && p.mainCollection.reference
    if (mainCol && mainCol.handle) {
      metafields.main_collection_handle = mainCol.handle
      metafields.main_collection_title = mainCol.title || ''
    }
    var linkedEyewear = []
    function pushLinkedEyewear(node) {
      if (!node || !node.handle) return
      var linkedMain = node.mainCollection && node.mainCollection.reference
      linkedEyewear.push({
        handle: node.handle,
        tags: node.tags || [],
        main_collection_handle: (linkedMain && linkedMain.handle) || '',
        main_collection_title: (linkedMain && linkedMain.title) || ''
      })
    }
    if (p.linkedEyewear) {
      if (p.linkedEyewear.reference) pushLinkedEyewear(p.linkedEyewear.reference)
      var linkedNodes = p.linkedEyewear.references && p.linkedEyewear.references.nodes
      ;(linkedNodes || []).forEach(pushLinkedEyewear)
    }
    if (linkedEyewear.length) metafields.linked_eyewear = linkedEyewear

    return {
      id: gidToId(p.id),
      handle: p.handle,
      title: p.title,
      product_type: p.productType,
      body_html: p.descriptionHtml,
      tags: p.tags || [],
      available: !!p.availableForSale,
      variants: variants,
      images: images,
      media: media,
      featured_image: p.featuredImage
        ? { src: p.featuredImage.url, alt: p.featuredImage.altText || '' }
        : images[0] || null,
      metafields: metafields
    }
  }

  function normalizeCollection(c) {
    if (!c) return null
    var products = (c.products && c.products.nodes ? c.products.nodes : []).map(function (p) {
      return normalizeProduct({
        id: p.id,
        handle: p.handle,
        title: p.title,
        productType: p.productType,
        descriptionHtml: '',
        tags: p.tags,
        availableForSale: p.availableForSale,
        featuredImage: p.featuredImage,
        // Rx frame slider needs every _01_/_02_ lens-color shot, not just variant images. Videos
        // come along for their alt, which names their lens color (Shopify renames video files).
        media: {
          nodes: (p.images && p.images.nodes ? p.images.nodes : [])
            .map(function (im) {
              return { mediaContentType: 'IMAGE', alt: im.altText || '', image: im }
            })
            .concat(
              (p.media && p.media.nodes ? p.media.nodes : []).filter(function (m) {
                return m.mediaContentType === 'VIDEO'
              })
            )
        },
        variants: p.variants,
        subtitle: null,
        mainCollection: null
      })
    })
    return {
      id: gidToId(c.id),
      handle: c.handle,
      title: c.title,
      products: products,
      products_count: products.length
    }
  }

  var productCache = new Map()
  var collectionCache = new Map()

  // Follow variants.pageInfo until every variant is loaded (Storefront caps a page at 250).
  function fetchRemainingVariants(handle, data) {
    var conn = data && data.product && data.product.variants
    if (!conn || !conn.pageInfo || !conn.pageInfo.hasNextPage) return data
    var nodes = (conn.nodes || []).slice()
    function next(after, guard) {
      if (!after || guard > 10) return Promise.resolve()
      return graphql(PRODUCT_VARIANTS_PAGE_QUERY, { handle: handle, after: after }).then(function (page) {
        var pc = page && page.product && page.product.variants
        if (!pc) return
        nodes = nodes.concat(pc.nodes || [])
        if (pc.pageInfo && pc.pageInfo.hasNextPage) return next(pc.pageInfo.endCursor, guard + 1)
      })
    }
    return next(conn.pageInfo.endCursor, 0).then(function () {
      var product = Object.assign({}, data.product, {
        variants: { nodes: nodes, pageInfo: { hasNextPage: false, endCursor: null } }
      })
      return Object.assign({}, data, { product: product })
    })
  }

  function productByHandle(handle) {
    if (!enabled() || !handle) return Promise.reject(new Error('storefront disabled/no handle'))
    if (productCache.has(handle)) return productCache.get(handle)
    var promise = graphql(PRODUCT_QUERY, { handle: handle })
      .then(function (data) {
        return fetchRemainingVariants(handle, data)
      })
      .then(function (data) {
        var product = normalizeProduct(data && data.product)
        if (!product) productCache.delete(handle)
        return product
      })
      .catch(function (err) {
        productCache.delete(handle)
        throw err
      })
    productCache.set(handle, promise)
    return promise
  }

  function productJsonByHandle(handle) {
    return productByHandle(handle).then(function (product) {
      return product ? { product: product } : null
    })
  }

  function collectionByHandle(handle) {
    if (!enabled() || !handle) return Promise.reject(new Error('storefront disabled/no handle'))
    if (collectionCache.has(handle)) return collectionCache.get(handle)
    var promise = graphql(COLLECTION_QUERY, { handle: handle })
      .then(function (data) {
        return normalizeCollection(data && data.collection)
      })
      .catch(function (err) {
        collectionCache.delete(handle)
        throw err
      })
    collectionCache.set(handle, promise)
    return promise
  }

  function resolveCollectionHandleForProduct(product) {
    if (!product) return ''
    var main = product.metafields && product.metafields.main_collection_handle
    var handle = main || frameCollectionHandleFromTags(product.tags)
    return getSizeBaseHandle(handle)
  }

  function sizeCollectionHandleFromFamily(familyHandle, productHandle) {
    if (!familyHandle) return ''
    var key = getSizeKeyFromHandle(productHandle)
    if (key === 'xl') return familyHandle + '-xl'
    if (key === 'xxl') return familyHandle + '-xxl'
    return familyHandle
  }

  function uniqueColorBasesFromProducts(products) {
    var seen = {}
    var bases = []
    ;(products || []).forEach(function (p) {
      var b = getSizeBaseHandle(p && p.handle)
      if (!b || seen[b]) return
      seen[b] = true
      bases.push(b)
    })
    return bases
  }

  function findProductByHandle(handle, collections, currentProduct) {
    if (!handle) return null
    if (currentProduct && currentProduct.handle === handle) return currentProduct
    for (var i = 0; i < collections.length; i++) {
      var products = collections[i] && collections[i].products
      if (!products) continue
      for (var j = 0; j < products.length; j++) {
        if (products[j] && products[j].handle === handle) return products[j]
      }
    }
    return null
  }

  function resolveSizedColorProducts(product, familyCol, sizeCol) {
    var key = getSizeKeyFromHandle(product.handle)
    var cols = [sizeCol, familyCol]
    var bases = uniqueColorBasesFromProducts(
      [].concat((familyCol && familyCol.products) || [], (sizeCol && sizeCol.products) || [])
    )
    return Promise.all(
      bases.map(function (base) {
        var sizedHandle = getSizeHandleForKey(base, key)
        var found = findProductByHandle(sizedHandle, cols, product)
        if (found) return Promise.resolve(found)
        return productByHandle(sizedHandle).catch(function () {
          return null
        })
      })
    ).then(function (list) {
      return list.filter(Boolean)
    })
  }

  function caretUrl(dir) {
    var wantRight = dir === 'right' || dir === 'next'
    if (wantRight) {
      if (cfg && cfg.caretRight) return cfg.caretRight
      var existingRight =
        typeof document !== 'undefined' &&
        document.querySelector('.other-products-slider-wrapper .pdp-next img, .thumbnail-wrapper .pdp-next img')
      if (existingRight && existingRight.src) return existingRight.src
      // Fallback: flip left caret via derived right asset path
      var left = caretUrl('left')
      return left ? left.replace('left-caret', 'right-caret') : ''
    }
    if (cfg && cfg.caretLeft) return cfg.caretLeft
    var existing =
      typeof document !== 'undefined' &&
      document.querySelector('.other-products-slider-wrapper .pdp-prev img, .thumbnail-wrapper .pdp-prev img')
    return existing ? existing.src : ''
  }

  function caretImgHtml(dir) {
    var src = escapeHtml(caretUrl(dir))
    if (!src) return ''
    var alt = dir === 'right' || dir === 'next' ? 'Right caret' : 'Left caret'
    return '<img src="' + src + '" alt="' + alt + '" width="16" height="16">'
  }

  function fmtMoneyFromDecimal(priceStr) {
    var n = parseFloat(priceStr)
    if (isNaN(n)) return ''
    var cents = Math.round(n * 100)
    var moneyFormat =
      (typeof document !== 'undefined' && document.body && document.body.getAttribute('data-money-format')) ||
      (typeof Shopify !== 'undefined' && Shopify.money_format) ||
      (global.SEP_DESIGN_PDP && global.SEP_DESIGN_PDP.moneyFormat) ||
      (global.SEP_DESIGN_OPH && global.SEP_DESIGN_OPH.moneyFormat) ||
      ''
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoneyOrigin === 'function') {
      return Shopify.formatMoneyOrigin(String(cents), moneyFormat)
    }
    return '$' + (cents / 100).toFixed(2)
  }

  function buildAtcHtml(product) {
    if (!product) return ''
    var variant = (product.variants || []).find(function (v) {
      return v.available
    }) || (product.variants && product.variants[0]) || null
    var soldOut = !variant || !variant.available
    var pt = product.product_type
    var inner
    if (soldOut && pt === 'Safety' && variant) {
      inner = '<div class="atc-button atc-button--notify">NOTIFY ME WHEN AVAILABLE</div>'
    } else if (soldOut) {
      inner = '<div class="atc-button">SOLD OUT</div>'
    } else if (pt === 'Prescription') {
      inner = '<div class="atc-button">CHOOSE YOUR LENSES</div>'
    } else if (pt === 'Safety') {
      inner =
        '<div class="atc-button"><span class="atc-button__label">ADD TO CART</span><span class="atc-button__amt" data-safety-atc-amt></span></div>'
    } else if (pt === 'Ophthalmic') {
      inner = '<div class="atc-button">ADD TO CART</div>'
    } else {
      var priceHtml
      if (pt === 'Sunglasses') {
        var disc = variant.price != null ? (parseFloat(variant.price) * 0.7).toFixed(2) : null
        priceHtml =
          '<span class="price-block price">' +
          escapeHtml(fmtMoneyFromDecimal(disc)) +
          '</span>' +
          '<span class="price-block compare-at-price">' +
          escapeHtml(fmtMoneyFromDecimal(variant.price)) +
          '</span>'
      } else {
        priceHtml = '<span class="price-block price">' + escapeHtml(fmtMoneyFromDecimal(variant.price)) + '</span>'
        if (variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price)) {
          priceHtml +=
            '<span class="price-block compare-at-price">' +
            escapeHtml(fmtMoneyFromDecimal(variant.compare_at_price)) +
            '</span>'
        }
      }
      inner =
        '<div class="atc-button">' +
        '<span class="atc-button__label">ADD TO CART</span>' +
        '<span class="atc-button__prices">' +
        priceHtml +
        '</span>' +
        '</div>'
    }
    return (
      '<div class="product__information_block atc" product_id="' +
      escapeHtml(product.id) +
      '">' +
      inner +
      '</div>'
    )
  }

  var SLIM_CASE_THUMB = 'https://neveneyewear.com/cdn/shop/files/image_2_500x.jpg?v=1720036674'
  var SLIM_CASE_FULL = 'https://neveneyewear.com/cdn/shop/files/image_2_2000x.jpg?v=1720036674'

  function buildMainSliderHtml(product) {
    if (!product) return ''
    var handle = product.handle
    var pt = (product.product_type || '').toLowerCase()
    var media = (product.media || []).filter(function (m) {
      return !(m.alt && m.alt.indexOf('FIT-GUIDE') !== -1)
    })
    if (!media.length && Array.isArray(product.images)) {
      media = product.images
        .filter(function (im) {
          return im && im.src
        })
        .map(function (im) {
          return {
            src: im.src,
            alt: im.alt || '',
            width: im.width,
            height: im.height,
            media_type: 'image'
          }
        })
    }
    var slideCount = media.length
    if (pt === 'prescription' || pt === 'ophthalmic') slideCount += 1
    var hideNav = slideCount <= 1 ? ' hidden' : ''
    var caretPrevImg = caretImgHtml('left')
    var caretNextImg = caretImgHtml('right')

    var thumbSlides = ''
    var mainSlides = ''
    media.forEach(function (m, i) {
      var alt = escapeHtml(m.alt || '')
      var thumbSrc = escapeHtml(imageUrl(m.src, 500))
      var displaySrc = escapeHtml(imageUrl(m.src, 1100))
      var fullSrc = escapeHtml(imageUrl(m.src, 2000))
      var imgLoadAttrs = i === 0 ? 'loading="eager" fetchpriority="high"' : 'loading="lazy"'
      var srcDown = (m.src || '').toLowerCase()
      var coverClass =
        srcDown.indexOf('lifestyle') !== -1 || srcDown.indexOf('preview_images') !== -1
          ? ' class="sep-thumb-img--cover"'
          : ''
      var badge = ''
      if (m.media_type === 'video') badge = '<span class="media-badge" aria-hidden="true"></span>'
      else if (m.media_type === 'model') badge = '<span class="media-badge" aria-hidden="true"></span>'

      var mediaAttrs =
        ' data-media-type="' +
        escapeHtml(m.media_type || 'image') +
        '"' +
        (m.media_type === 'video' ? ' data-media-alt="' + alt + '"' : '')

      thumbSlides +=
        '<div class="swiper-slide"' + mediaAttrs + '>' +
        '<div class="image-wrapper" data-index="' +
        i +
        '">' +
        '<img src="' +
        thumbSrc +
        '" alt="' +
        alt +
        '" loading="lazy" width="500" height="500"' +
        coverClass +
        '>' +
        badge +
        '</div>' +
        '</div>'

      if (m.media_type === 'video' && m.sources && m.sources.length) {
        var sources = m.sources
          .map(function (s) {
            return '<source src="' + escapeHtml(s.url) + '" type="' + escapeHtml(s.mime_type || '') + '">'
          })
          .join('')
        mainSlides +=
          '<div class="swiper-slide"' + mediaAttrs + '>' +
          '<div class="data-html5-video" data-html5-video data-index="' +
          i +
          '">' +
          '<video controls playsinline poster="' +
          thumbSrc +
          '">' +
          sources +
          '</video>' +
          '</div>' +
          '</div>'
      } else {
        mainSlides +=
          '<div class="swiper-slide">' +
          '<div class="image-wrapper" data-fancybox="' +
          escapeHtml(product.id) +
          '" data-src="' +
          fullSrc +
          '" data-caption="' +
          alt +
          '" data-index="' +
          i +
          '">' +
          '<img src="' +
          displaySrc +
          '" alt="' +
          alt +
          '" ' +
          imgLoadAttrs +
          ' decoding="async" width="' +
          escapeHtml(m.width || '') +
          '" height="' +
          escapeHtml(m.height || '') +
          '">' +
          '</div>' +
          '</div>'
      }
    })

    if (pt === 'prescription' || pt === 'ophthalmic') {
      thumbSlides +=
        '<div class="swiper-slide" data-special-slide="slim-hard-case">' +
        '<div class="image-wrapper" data-index="">' +
        '<img src="' +
        SLIM_CASE_THUMB +
        '" alt="Slim Hard Case" loading="lazy" width="500" height="500">' +
        '</div>' +
        '</div>'
      mainSlides +=
        '<div class="swiper-slide" data-special-slide="slim-hard-case">' +
        '<div class="image-wrapper" data-fancybox="' +
        escapeHtml(product.id) +
        '" data-src="' +
        SLIM_CASE_FULL +
        '" data-caption="Slim Hard Case" data-index="">' +
        '<img src="' +
        SLIM_CASE_THUMB +
        '" alt="Slim Hard Case" loading="lazy" width="500" height="500">' +
        '</div>' +
        '</div>'
    }

    var glasson = ''
    if (pt === 'sunglasses' || pt === 'blue light' || pt === 'prescription' || pt === 'ophthalmic' || pt === 'safety') {
      glasson = '<div class="atc_app_blocks handle-' + escapeHtml(handle) + '"></div>'
    }

    return (
      '<div class="product__sep-design__mainSlider swiper-' +
      escapeHtml(handle) +
      '">' +
      '<div class="product-gallery-media">' +
      '<div class="thumbnail-wrapper">' +
      '<div class="pdp-prev' +
      hideNav +
      '">' +
      caretPrevImg +
      '</div>' +
      '<div class="swiper thumbnail-swiper-' +
      escapeHtml(handle) +
      hideNav +
      '" data-product-type="' +
      escapeHtml(pt) +
      '">' +
      '<div class="swiper-wrapper">' +
      thumbSlides +
      '</div>' +
      '</div>' +
      '<div class="pdp-next' +
      hideNav +
      '">' +
      caretNextImg +
      '</div>' +
      '</div>' +
      '<div class="mainslider-wrapper">' +
      '<div class="swiper main-swiper-' +
      escapeHtml(handle) +
      '" data-product-type="' +
      escapeHtml(pt) +
      '">' +
      '<div class="swiper-wrapper">' +
      mainSlides +
      '</div>' +
      '</div>' +
      glasson +
      '<div class="sep-main-pagination" aria-label="Product image pagination"></div>' +
      '</div>' +
      '</div>' +
      '</div>'
    )
  }

  function initMainSliderForHandle(handle) {
    if (!handle || typeof Swiper === 'undefined') return null
    var root =
      document.querySelector(
        '.product__images > .product__sep-design__mainSlider.swiper-' + handle + ':not(.hidden)'
      ) ||
      document.querySelector('.product__images > .product__sep-design__mainSlider.swiper-' + handle) ||
      document.querySelector('.product__sep-design__mainSlider.swiper-' + handle)
    if (!root) return null
    if (global.pdp && global.pdp.swipers && global.pdp.swipers[handle] && global.pdp.swipers[handle].swiper) {
      var liveEl = global.pdp.swipers[handle].swiper.el
      if (liveEl && liveEl.isConnected && liveEl.clientWidth >= 2 && root.contains(liveEl)) {
        return global.pdp.swipers[handle]
      }
      try {
        if (global.pdp.swipers[handle].swiper.destroy) global.pdp.swipers[handle].swiper.destroy(true, true)
      } catch (e) {}
      try {
        if (global.pdp.swipers[handle].thumbSwiper && global.pdp.swipers[handle].thumbSwiper.destroy) {
          global.pdp.swipers[handle].thumbSwiper.destroy(true, true)
        }
      } catch (e2) {}
      delete global.pdp.swipers[handle]
    }
    var thumbEl =
      root.querySelector('.thumbnail-swiper-' + handle) || root.querySelector('.thumbnail-wrapper .swiper')
    var mainEl = root.querySelector('.main-swiper-' + handle) || root.querySelector('.mainslider-wrapper > .swiper') || document.querySelector('.main-swiper-' + handle)
    if (!mainEl) return null
    var initialSlide = 0
    if (
      document.querySelector('.sep-design-pdp.is-side-shield-pdp') &&
      global.pdp &&
      global.pdp.currentProduct &&
      global.pdp.currentProduct.handle === handle &&
      global.pdp.selectedVariant
    ) {
      var feat = global.pdp.selectedVariant.featured_image
      var featSrc = feat && (feat.src || feat)
      if (featSrc) {
        var stem = String(featSrc)
          .split('?')[0]
          .split('/')
          .pop()
          .replace(/_\d+x(\d+)?(?=\.[^.]+$)/i, '')
          .toLowerCase()
        var imgs = mainEl.querySelectorAll('.swiper-slide img')
        for (var si = 0; si < imgs.length; si++) {
          var file = String(imgs[si].getAttribute('src') || '')
            .split('?')[0]
            .split('/')
            .pop()
            .replace(/_\d+x(\d+)?(?=\.[^.]+$)/i, '')
            .toLowerCase()
          if (file === stem) {
            initialSlide = si
            break
          }
        }
      }
    }
    var nextEl = root.querySelector('.pdp-next')
    var prevEl = root.querySelector('.pdp-prev')
    var thumbSwiper = thumbEl
      ? new Swiper(thumbEl, {
          navigation: nextEl && prevEl ? { nextEl: nextEl, prevEl: prevEl } : undefined,
          freeMode: true,
          slidesPerView: 2.2,
          spaceBetween: 12,
          initialSlide: initialSlide,
          breakpoints: {
            798: { direction: 'vertical', slidesPerView: 5, spaceBetween: 12 }
          }
        })
      : null
    var swiper = new Swiper(mainEl, {
      slidesPerView: 1,
      initialSlide: initialSlide,
      thumbs: thumbSwiper ? { swiper: thumbSwiper } : undefined
    })
    var paginationEl = root.querySelector('.sep-main-pagination')
    function syncSepMainPagination() {
      if (!paginationEl || !swiper || !swiper.el) return
      var slides = Array.from(swiper.el.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide'))
      var visible = slides.filter(function (slide) {
        return !slide.classList.contains('hidden')
      })
      paginationEl.classList.toggle('is-single', visible.length <= 1)
      var activeVisibleIndex = visible.findIndex(function (slide) {
        return slide.classList.contains('swiper-slide-active')
      })
      if (activeVisibleIndex < 0) activeVisibleIndex = visible.indexOf(slides[swiper.activeIndex])
      if (activeVisibleIndex < 0) activeVisibleIndex = 0
      paginationEl.innerHTML = visible
        .map(function (slide, index) {
          var slideIndex = slides.indexOf(slide)
          var onClass = index === activeVisibleIndex ? ' on' : ''
          return (
            '<button type="button" class="sep-main-dot' +
            onClass +
            '" data-slide-index="' +
            slideIndex +
            '" aria-label="Image ' +
            (index + 1) +
            '"></button>'
          )
        })
        .join('')
    }
    if (paginationEl) {
      paginationEl.addEventListener('click', function (event) {
        var button = event.target.closest('[data-slide-index]')
        if (!button) return
        var slideIndex = Number(button.getAttribute('data-slide-index'))
        if (!Number.isFinite(slideIndex) || slideIndex < 0) return
        swiper.slideTo(slideIndex)
      })
      swiper.on('slideChange', syncSepMainPagination)
      swiper.on('update', syncSepMainPagination)
      syncSepMainPagination()
    }
    if (!global.pdp) global.pdp = {}
    if (!global.pdp.swipers) global.pdp.swipers = {}
    global.pdp.swipers[handle] = { swiper: swiper, thumbSwiper: thumbSwiper, syncPagination: syncSepMainPagination }
    return global.pdp.swipers[handle]
  }

  function buildSizeSelectorHtml(product, sizeProductsByKey) {
    var pt = product.product_type
    if (pt !== 'Sunglasses' && pt !== 'Prescription' && pt !== 'Safety') return ''
    var family = getSizeFamilyHandles(product.handle)
    var byKey = Object.assign({ std: null, xl: null, xxl: null }, sizeProductsByKey || {})
    byKey[family.current] = product

    function widthOf(p, fallback) {
      if (!p || !p.handle) return fallback || ''
      return tagValue(p.tags, '_frame_width:').replace(/^number:/, '') || fallback || ''
    }
    function sizeOf(p, fallback) {
      if (!p || !p.handle) return fallback || ''
      return tagValue(p.tags, '_frame_size:').replace(/_/g, ' ') || fallback || ''
    }

    var stdWidth = widthOf(byKey.std, '')
    var stdSize = sizeOf(byKey.std, '')
    var xlWidth = widthOf(byKey.xl, '')
    var xxlWidth = widthOf(byKey.xxl, '')

    var hasStd = !!(byKey.std && byKey.std.handle)
    var hasXl = !!(byKey.xl && byKey.xl.handle)
    var hasXxl = !!(byKey.xxl && byKey.xxl.handle)
    var availableCount = (hasStd ? 1 : 0) + (hasXl ? 1 : 0) + (hasXxl ? 1 : 0)
    var hasCp = availableCount > 1

    function roundMm(width) {
      if (!width) return ''
      var n = parseFloat(String(width).replace(/[^\d.-]/g, ''))
      if (!isFinite(n)) return ''
      return String(Math.round(n))
    }
    function helpText(sizeKey, width) {
      var mm = roundMm(width)
      var prefix = 'Our everyday fit'
      if (sizeKey === 'xl') prefix = 'For wider faces'
      if (sizeKey === 'xxl') prefix = 'Our widest frame yet'
      if (mm) return prefix + ' — ' + mm + 'mm.'
      return prefix + '.'
    }

    var stdMm = roundMm(stdWidth)
    var xlMm = roundMm(xlWidth)
    var xxlMm = roundMm(xxlWidth)
    var stdTitle = 'Standard'
    var xlTitle = 'XL'
    var xxlTitle = 'XXL'
    var stdLabel = stdMm ? 'Standard · ' + stdMm + 'mm' : 'Standard'
    var xlLabel = xlMm ? 'XL · ' + xlMm + 'mm' : 'XL'
    var xxlLabel = xxlMm ? 'XXL · ' + xxlMm + 'mm' : 'XXL'
    var stdDesc = stdMm ? stdMm + 'mm' : ''
    var xlDesc = xlMm ? xlMm + 'mm' : ''
    var xxlDesc = xxlMm ? xxlMm + 'mm' : ''
    var stdHelp = helpText('std', stdWidth)
    var xlHelp = helpText('xl', xlWidth)
    var xxlHelp = helpText('xxl', xxlWidth)

    var activeLabel =
      family.current === 'xxl' ? xxlLabel : family.current === 'xl' ? xlLabel : stdLabel
    var activeHelp =
      family.current === 'xxl' ? xxlHelp : family.current === 'xl' ? xlHelp : stdHelp

    function hasNewBadge(p) {
      var list = (p && p.tags) || []
      if (typeof list === 'string') {
        list = list.split(',').map(function (s) { return s.trim() })
      }
      for (var i = 0; i < list.length; i++) {
        if (list[i] === '_size_badge:new') return true
      }
      return false
    }

    var stdHasNew = hasNewBadge(byKey.std)
    var xlHasNew = hasNewBadge(byKey.xl)
    var xxlHasNew = hasNewBadge(byKey.xxl)

    function btnHtml(sizeKey, title, label, desc, help, available, showNew) {
      return (
        '<button type="button" class="pdp-size-selector__btn' +
        (family.current === sizeKey ? ' on' : '') +
        (available ? '' : ' hidden') +
        '" data-size="' +
        sizeKey +
        '" data-label="' +
        escapeHtml(label) +
        '" data-help="' +
        escapeHtml(help) +
        '">' +
        (showNew ? '<span class="pdp-size-selector__new">NEW</span>' : '') +
        '<span class="pdp-size-selector__t">' +
        escapeHtml(title) +
        '</span>' +
        '<span class="pdp-size-selector__d"' +
        (desc ? '' : ' hidden') +
        '>' +
        escapeHtml(desc) +
        '</span>' +
        '</button>'
      )
    }

    return (
      '<div class="pdp-size-selector' +
      (hasCp ? '' : ' hidden') +
      (hasXxl ? ' pdp-size-selector--tri' : '') +
      '" data-size-selector data-has-counterpart="' +
      (hasCp ? 'true' : 'false') +
      '" data-has-xxl="' +
      (hasXxl ? 'true' : 'false') +
      '" data-std-handle="' +
      escapeHtml(family.std) +
      '" data-xl-handle="' +
      escapeHtml(family.xl) +
      '" data-xxl-handle="' +
      escapeHtml(family.xxl) +
      '" data-std-width="' +
      escapeHtml(stdWidth) +
      '" data-std-size="' +
      escapeHtml(stdSize) +
      '" data-xl-width="' +
      escapeHtml(xlWidth) +
      '" data-xxl-width="' +
      escapeHtml(xxlWidth) +
      '" data-std-badge-new="' +
      (stdHasNew ? 'true' : 'false') +
      '" data-xl-badge-new="' +
      (xlHasNew ? 'true' : 'false') +
      '" data-xxl-badge-new="' +
      (xxlHasNew ? 'true' : 'false') +
      '">' +
      '<div class="pdp-size-selector__lab">' +
      '<span class="pdp-size-selector__k">Size</span>' +
      '<span class="pdp-size-selector__v" data-size-label>' +
      escapeHtml(activeLabel) +
      '</span>' +
      '</div>' +
      '<div class="pdp-size-selector__seg" role="group" aria-label="Frame size" data-n="' +
      availableCount +
      '">' +
      btnHtml('std', stdTitle, stdLabel, stdDesc, stdHelp, hasStd, stdHasNew) +
      btnHtml('xl', xlTitle, xlLabel, xlDesc, xlHelp, hasXl, xlHasNew) +
      btnHtml('xxl', xxlTitle, xxlLabel, xxlDesc, xxlHelp, hasXxl, xxlHasNew) +
      '</div>' +
      '<div class="pdp-size-selector__help">' +
      '<span data-size-help>' +
      escapeHtml(activeHelp) +
      '</span>' +
      '<a href="' +
      sizeSelectorFitHref() +
      '" data-pdp-open-fit-modal>Find your fit →</a>' +
      '</div>' +
      '</div>'
    )
  }

  function uniqueColorwayCount(products) {
    var seen = {}
    var n = 0
    ;(products || []).forEach(function (p) {
      var base = getSizeBaseHandle(p && p.handle)
      if (!base || seen[base]) return
      seen[base] = true
      n += 1
    })
    return n
  }

  function otherProductsHeaderTitle(collection, product) {
    if (!collection) return ''
    if (product.product_type === 'Prescription' || product.product_type === 'Ophthalmic') return ''
    var title = (collection.title || '').toUpperCase()
    var check = (collection.title || '').toLowerCase().replace(/ō/g, 'o')
    if (check.indexOf('shogun') !== -1 && collection.products && collection.products[0]) {
      var display = tagValue(collection.products[0].tags, '_frame_display_name:')
      if (display) title = display.toUpperCase()
    }
    return title
  }

  function rxFrameDisplayName(collection, product) {
    var tags = (product && product.tags) || []
    var name =
      tagValue(tags, '_oph_frame_display_name:') ||
      tagValue(tags, '_rx_frame_display_name:') ||
      tagValue(tags, '_frame_display_name:') ||
      (collection && collection.title) ||
      ''
    return name.toUpperCase()
  }

  function buildOtherProductsHtml(product, collection, sizeProductsByKey) {
    if (!product || !collection) return ''
    var ch = collection.handle
    var pt = product.product_type
    var ptLower = (pt || '').toLowerCase()
    var caretPrevImg = caretImgHtml('left')
    var caretNextImg = caretImgHtml('right')
    var uniqueColors = uniqueColorwayCount(collection.products)
    var isSolo = pt !== 'Prescription' && pt !== 'Ophthalmic' && uniqueColors <= 1
    var header
    var also = Math.max(uniqueColors - 1, 0)
    if (pt === 'Prescription' || pt === 'Ophthalmic') {
      header =
        '<div class="other-products-header-wrapper">' +
        '<div class="other-products-header">' +
        '<div class="other-products-header-title">' +
        escapeHtml(rxFrameDisplayName(collection, product)) +
        '</div>' +
        (also > 0 ? '<div class="other-products-header-also-in">+' + also + ' Colors</div>' : '') +
        '<div class="other-products-header-xl-regular"></div>' +
        '</div>' +
        '</div>'
    } else {
      if (uniqueColors > 1) {
        var hTitle = otherProductsHeaderTitle(collection, product) || (collection.title || '').toUpperCase()
        header =
          '<div class="other-products-header-wrapper">' +
          '<div class="other-products-header">' +
          '<div class="other-products-header-title"><a href="/collections/' +
          escapeHtml(ch) +
          '">' +
          escapeHtml(hTitle) +
          '</a></div>' +
          (also > 0
            ? '<div class="other-products-header-also-in">+' + also + ' Colors</div>'
            : '') +
          '</div>' +
          '</div>'
      } else {
        header = ''
      }
    }

    var slides = (collection.products || [])
      .map(function (p) {
        var feat = (p.featured_image && p.featured_image.src) || (p.images && p.images[0] && p.images[0].src) || ''
        var url02 = imageUrl(feat, 400)
        var url01 = url02.replace('_02.', '_01.').replace('_02_', '_01_')
        return (
          '<div class="swiper-slide" data-product-handle="' +
          escapeHtml(p.handle) +
          '" title="' +
          escapeHtml(p.title) +
          '">' +
          '<div class="image-wrapper">' +
          '<img class="other-products-image-02" src="' +
          escapeHtml(url02) +
          '" alt="" loading="lazy" width="400" height="400">' +
          '<img class="other-products-image-01" src="' +
          escapeHtml(url01) +
          '" alt="" loading="lazy" width="400" height="400">' +
          '</div>' +
          '<div class="other-products-name">' +
          escapeHtml(p.title) +
          '</div>' +
          '</div>'
        )
      })
      .join('')

    var sizeSel = buildSizeSelectorHtml(product, sizeProductsByKey)
    var isFew = !isSolo && uniqueColors > 0 && uniqueColors < 6

    return (
      '<div class="other-products-container other-products-swiper-container-' +
      escapeHtml(ch) +
      (isSolo ? ' other-products-container--solo' : '') +
      (isFew ? ' other-products-container--few' : '') +
      '">' +
      header +
      '<div class="other-products-slider-wrapper">' +
      '<div class="swiper other-products-swiper-' +
      escapeHtml(ch) +
      '" data-product-type="' +
      escapeHtml(ptLower) +
      '">' +
      '<div class="swiper-wrapper">' +
      slides +
      '</div>' +
      '</div>' +
      '<div class="pdp-prev">' +
      caretPrevImg +
      '</div>' +
      '<div class="pdp-next">' +
      caretNextImg +
      '</div>' +
      '</div>' +
      sizeSel +
      '</div>'
    )
  }

  function mountOtherProductsSwiper(collectionHandle) {
    if (!collectionHandle || typeof Swiper === 'undefined') return null
    if (global.pdp && global.pdp.swipers && global.pdp.swipers[collectionHandle] && global.pdp.swipers[collectionHandle].swiper) {
      return global.pdp.swipers[collectionHandle]
    }
    var isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 798px)').matches
    var swiperEl = document.querySelector('.other-products-swiper-' + collectionHandle)
    var containerEl = swiperEl && swiperEl.closest('.other-products-container')
    var slideCount = swiperEl ? swiperEl.querySelectorAll('.swiper-slide').length : 0
    var trackWidth = 0
    if (containerEl) {
      var wrap = containerEl.querySelector('.other-products-slider-wrapper')
      trackWidth = (wrap && wrap.clientWidth ? wrap.clientWidth : 0) - 50
    }
    var filled = slideCount * 60 + Math.max(0, slideCount - 1) * 6
    var few = slideCount > 0 && (trackWidth <= 0 || filled < trackWidth)
    if (containerEl) containerEl.classList.toggle('other-products-container--few', few)
    var opts = {
      freeMode: true,
      spaceBetween: 6,
      slidesPerView: few ? 'auto' : isMobile ? 6 : 1,
      navigation: {
        nextEl: '.other-products-swiper-container-' + collectionHandle + ' .pdp-next',
        prevEl: '.other-products-swiper-container-' + collectionHandle + ' .pdp-prev'
      }
    }
    if (!few && !isMobile) {
      opts.breakpointsBase = 'container'
      opts.breakpoints = {
        120: { slidesPerView: 2 },
        180: { slidesPerView: 3 },
        240: { slidesPerView: 4 },
        300: { slidesPerView: 5 },
        360: { slidesPerView: 6 },
        420: { slidesPerView: 7 },
        480: { slidesPerView: 8 },
        560: { slidesPerView: 9 },
        620: { slidesPerView: 10 },
        680: { slidesPerView: 11 },
        740: { slidesPerView: 12 },
        800: { slidesPerView: 13 },
        860: { slidesPerView: 14 },
        920: { slidesPerView: 15 }
      }
    }
    var swiper = new Swiper('.other-products-swiper-' + collectionHandle, opts)
    if (!global.pdp) global.pdp = {}
    if (!global.pdp.swipers) global.pdp.swipers = {}
    global.pdp.swipers[collectionHandle] = { swiper: swiper }

    var slides = document.querySelectorAll('.other-products-swiper-' + collectionHandle + ' .swiper-slide')
    slides.forEach(function (slide) {
      slide.addEventListener('click', function () {
        if (typeof global.isPdpProductSwitchBusy === 'function' && global.isPdpProductSwitchBusy()) return
        var baseHandle = slide.getAttribute('data-product-handle')
        var handle =
          typeof global.resolveOphSplitHandleForNavigation === 'function'
            ? global.resolveOphSplitHandleForNavigation(baseHandle)
            : baseHandle
        if (!handle || (global.pdp.currentProduct && handle === global.pdp.currentProduct.handle)) return
        if (typeof global.beginPdpProductSwitch === 'function' && !global.beginPdpProductSwitch('frame', handle)) return
        slides.forEach(function (s) {
          s.classList.remove('swiper-slide-custom-active')
        })
        slide.classList.add('swiper-slide-custom-active')
        if (typeof setState !== 'undefined' && setState['currentProductHandle']) {
          setState['currentProductHandle'](handle)
        }
      })
    })
    return global.pdp.swipers[collectionHandle]
  }

  /**
   * Fetch product + collection (+ size siblings) and mount other-products UI.
   * Returns { collectionHandle, products } or null.
   */
  function loadOtherProductsForHandle(handle) {
    if (!enabled() || !handle) return Promise.resolve(null)
    return productByHandle(handle).then(function (product) {
      if (!product) return null
      var colHandle = resolveCollectionHandleForProduct(product)
      if (!colHandle) return null
      var sizeColHandle = sizeCollectionHandleFromFamily(colHandle, product.handle)
      var family =
        product.product_type === 'Sunglasses' ||
        product.product_type === 'Prescription' ||
        product.product_type === 'Safety'
          ? getSizeFamilyHandles(product.handle)
          : null
      var sizeKeys = family ? ['std', 'xl', 'xxl'] : []
      var tasks = [
        collectionByHandle(colHandle).catch(function () {
          return null
        })
      ]
      if (sizeColHandle && sizeColHandle !== colHandle) {
        tasks.push(
          collectionByHandle(sizeColHandle).catch(function () {
            return null
          })
        )
      } else {
        tasks.push(Promise.resolve(null))
      }
      sizeKeys.forEach(function (key) {
        var siblingHandle = family[key]
        if (siblingHandle && siblingHandle !== product.handle) {
          tasks.push(
            productByHandle(siblingHandle)
              .then(function (p) {
                return { key: key, product: p }
              })
              .catch(function () {
                return { key: key, product: null }
              })
          )
        } else {
          tasks.push(Promise.resolve({ key: key, product: key === family.current ? product : null }))
        }
      })
      return Promise.all(tasks).then(function (results) {
        var familyCol = results[0]
        var sizeCol = results[1]
        if (!familyCol && !sizeCol) return null
        var sizeProductsByKey = { std: null, xl: null, xxl: null }
        if (family) sizeProductsByKey[family.current] = product
        for (var i = 2; i < results.length; i++) {
          var entry = results[i]
          if (entry && entry.key) sizeProductsByKey[entry.key] = entry.product
        }
        return resolveSizedColorProducts(product, familyCol, sizeCol).then(function (sizedProducts) {
          var titleSource = familyCol || sizeCol
          var uiHandle = (sizeCol && sizeCol.handle) || sizeColHandle || (familyCol && familyCol.handle) || colHandle
          var collection = {
            id: (sizeCol && sizeCol.id) || (familyCol && familyCol.id),
            handle: uiHandle,
            title: titleSource && titleSource.title,
            products: sizedProducts,
            products_count: sizedProducts.length
          }
          return {
            product: product,
            collection: collection,
            sizeProductsByKey: sizeProductsByKey,
            collectionHandle: collection.handle,
            html: buildOtherProductsHtml(product, collection, sizeProductsByKey)
          }
        })
      })
    })
  }

  global.sepStorefront = {
    _ready: true,
    enabled: enabled,
    graphql: graphql,
    productByHandle: productByHandle,
    productJsonByHandle: productJsonByHandle,
    collectionByHandle: collectionByHandle,
    resolveCollectionHandleForProduct: resolveCollectionHandleForProduct,
    counterpartHandleFrom: counterpartHandleFrom,
    buildAtcHtml: buildAtcHtml,
    buildMainSliderHtml: buildMainSliderHtml,
    initMainSliderForHandle: initMainSliderForHandle,
    buildOtherProductsHtml: buildOtherProductsHtml,
    mountOtherProductsSwiper: mountOtherProductsSwiper,
    loadOtherProductsForHandle: loadOtherProductsForHandle,
    gidToId: gidToId,
    moneyToPriceString: moneyToPriceString,
    imageUrl: imageUrl
  }
})(typeof window !== 'undefined' ? window : this)
